/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Helmet from 'react-helmet';
import classNames from 'classnames';
import colorUtils from 'color';
import { get } from 'lodash/fp';
import glamorous, { ThemeProvider } from 'glamorous';
import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming';
import { parse as parseQuery } from 'qs';
import { compose } from 'recompose';

import themeConfig from '../theme';
import alertSelector from '../selectors/alertSelector';
import useSheet from '../jss';
import { toggleCampaignModal } from '../actions/userActions';
import * as routeActions from '../actions/routeActions';
import * as UiActions from '../actions/uiActions';
import MobileMenu from '../components/menu/MobileMenu';
import MobileNav from '../components/menu/MobileNav';
import PlayUpCampaignModal, { checkRejected } from '../scenes/account/PlayUpCampaignModal';
import AcceptTermsModal from '../scenes/account/AcceptTermsModal';
import ContentModal from '../components/commons/ContentModal';
import HubMenu, { hubMenuConfig } from '../components/hubMenu/HubMenu';
import NotificationsDisplay from '../components/notifications/NotificationsDisplay';
import NotificationsProvider from '../components/notifications/NotificationsProvider';
import Responsive, { useIsMobile } from '../components/responsive/Responsive';
import SidebarLeft from './parts/SidebarLeft';
import SidebarRight from './parts/SidebarRight';
import Tappable from '../components/commons/Tappable';
import * as cssEase from '../constants/cssEase';
import MobileChat from '../components/chat/MobileChat';
import DesktopChat from '../components/chat/DesktopChat';

import TimerProvider from '../components/commons/TimerProvider';
import { postMessageToRN } from '../utils/domUtils';
import FriendsLoader from '../components/friends/common/FriendsLoader';
import { getReferrerCodeFromQuery, saveReferrerCode } from '../utils/userUtils';
import UpdateAppScreen from './parts/UpdateAppScreen';

function App(props: Props) {
  const {
    alert = undefined,
    chatEnabled = false,
    hasPlayUpCampaign,
    children,
    darkMode,
    location,
    displayHubMenu,
    showCampaignModal,
    isInvalid,
    isMobileChatOpen,
    isMobileMenuOpen,
    isNativeApp, // for old webview based ds app
    isNativeWebView, // for webviews inside the newer playup/ds app
    isPxcEnabled,
    isSigningIn,
    isSignedIn,
    onToggleMobileMenu,
    onToggleCampaignModal,
    saveUrlQuery,
    sheet: { classes },
    siteName,
    siteUrl,
    toggleChat,
    userId,
    updateAppPlatform
  } = props;
  const [contentModalIsOpen, setContentModalIsOpen] = React.useState(false);
  const [contentModalPage, setContentModalPage] = React.useState(null);
  const [offsetTop, setOffsetTop] = React.useState(0);

  React.useEffect(() => {
    // WebView (Android)
    document.addEventListener('message', (event = {}) => {});

    // WkWebView (iOS)
    window.receivedMessageFromReactNative = data => {};

    if (isNativeApp) {
      postMessageToRN({ mounted: true });
      setOffsetTop('80px');
    }

    const queryData = parseQuery(location.search, { ignoreQueryPrefix: true });

    saveUrlQuery(queryData);
    saveReferrerCode(getReferrerCodeFromQuery(queryData));
  }, []);

  React.useEffect(() => {
    if (userId) {
      if (isNativeApp) {
        postMessageToRN({ userId: userId });
      }
    }

    if (hasPlayUpCampaign && !showCampaignModal) {
      try {
        if (checkRejected()) {
          return;
        }
        window.setTimeout(() => onToggleCampaignModal(true), 3000);
      } catch (e) {
        // console.log('appboy was not initialized, maybe youre in the native app?');
      }
    }
  }, [isNativeApp, hasPlayUpCampaign, userId, onToggleCampaignModal]);

  const handleCloseCampaignModal = () => {
    onToggleCampaignModal(false);
  };

  const toggleContentModal = page => {
    setContentModalIsOpen(!contentModalIsOpen);
    setContentModalPage(!contentModalIsOpen ? page : null);
  };
  const pathName = location ? location.pathname : undefined;

  const isMobile = useIsMobile();

  // if (updateAppPlatform === 'ios' || updateAppPlatform === 'android') {
  //   return <UpdateAppScreen platform={updateAppPlatform} />;
  // }

  return (
    <ThemeProvider theme={themeConfig}>
      <EmotionThemeProvider theme={themeConfig}>
        <NotificationsProvider>
          <TimerProvider>
            <div style={{ paddingBottom: isMobile && !isNativeWebView ? '80px' : '0' }}>
              {displayHubMenu && <HubMenu siteUrl={siteUrl} />}
              {!isNativeWebView && (
                <Responsive mobile>
                  <MobileNav />
                </Responsive>
              )}
              <AppWrapper darkMode={darkMode} padTop={displayHubMenu ? hubMenuConfig.height : 0}>
                <Helmet titleTemplate={`%s | ${siteName}`} />
                {isPxcEnabled && isSigningIn && (
                  <LoadingOverlay>
                    <LoadingWrapper>
                      <FriendsLoader>Loading</FriendsLoader>
                    </LoadingWrapper>
                  </LoadingOverlay>
                )}
                <Responsive mobile>
                  <MobileMenu
                    offsetTop={offsetTop}
                    onClose={onToggleMobileMenu}
                    onTapContentLink={toggleContentModal}
                  />
                  {isSignedIn && chatEnabled && <MobileChat />}
                </Responsive>
                <Responsive desktop>{isSignedIn && chatEnabled && <DesktopChat />}</Responsive>
                <NotificationsDisplay />
                <div className="appBodyWrapper" style={{ paddingTop: `${offsetTop}px` }}>
                  <div
                    className={classNames('appBody', classes.appBody, {
                      isMobileMenuOpen,
                      isMobileChatOpen
                    })}
                  >
                    <Tappable
                      className={classNames(classes.mobileMenuOverlay, {
                        visible: isMobileMenuOpen || isMobileChatOpen
                      })}
                      onTap={isMobileMenuOpen ? onToggleMobileMenu : toggleChat}
                    />
                    {isInvalid ? <AcceptTermsModal /> : children}
                    {hasPlayUpCampaign && showCampaignModal && (
                      <PlayUpCampaignModal onCancel={handleCloseCampaignModal} />
                    )}
                  </div>
                  {(!pathName || pathName.includes('/fantasy-insider') === false) && (
                    <React.Fragment>
                      <Responsive desktop>
                        <SidebarLeft isSignedIn={isSignedIn} offsetTop={offsetTop} />
                      </Responsive>
                      <Responsive desktopLarge>
                        <SidebarRight offsetTop={offsetTop} />
                      </Responsive>
                    </React.Fragment>
                  )}
                </div>
                <ContentModal
                  isOpen={!isInvalid && contentModalIsOpen}
                  onCancel={toggleContentModal}
                  page={contentModalPage}
                />
              </AppWrapper>
            </div>
          </TimerProvider>
        </NotificationsProvider>
      </EmotionThemeProvider>
    </ThemeProvider>
  );
}

function mapStateToProps(state) {
  return {
    alert: alertSelector(state),
    chatEnabled: !!state.settings.chatURI,
    darkMode: state.settings.darkMode,
    displayHubMenu: state.settings.displayHubMenu,
    displayPromotionModal: state.settings.displayPromotionModal,
    fetchingUserData: state.user.fetchingUserData,
    isInvalid: state.user.isInvalid,
    hasPlayUpCampaign: !!get('user.currentUser.hasPlayUpCampaign', state),
    showCampaignModal: !!get('user.showCampaignModal', state),
    isMobileChatOpen: state.ui.isChatOpen,
    isMobileMenuOpen: state.ui.isMobileMenuOpen,
    isNativeApp: !!state.settings.nativeApp,
    isNativeWebView: state.ui.isNativeWebView,
    isPxcEnabled: !!state.settings.enablePxc,
    isSignedIn: state.user.isSignedIn,
    isSigningIn: state.user.isSigningIn,
    siteName: state.settings.siteName,
    siteUrl: state.settings.siteUrl,
    userId: get('user.currentUser.id', state),
    updateAppPlatform: get('native-app', state.route.query)
  };
}

const actions = {
  onToggleMobileMenu: UiActions.toggleMobileMenu,
  toggleChat: UiActions.toggleChat,
  saveUrlQuery: routeActions.saveUrlQuery,
  onToggleCampaignModal: toggleCampaignModal
};

function applyStyles(theme) {
  return {
    mobileMenuOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colorUtils(theme.color.basis1)
        .alpha(0.5)
        .toString(),
      opacity: 0,
      transition: 'opacity 0.5s',
      zIndex: theme.zIndex.menu,
      pointerEvents: 'none',
      '&.visible': {
        pointerEvents: 'auto',
        opacity: 1
      }
    },
    appBody: {
      transition: 'transform 0.20s',
      transitionTimingFunction: cssEase.Circ.easeInOut,
      '&.isMobileMenuOpen': {
        transform: 'translate3d(85%, 0, 0)'
      },
      '&.isMobileChatOpen': {
        transform: 'translate3d(-85%, 0, 0)'
      }
    }
  };
}

const connector = compose(withRouter, connect(mapStateToProps, actions), useSheet(applyStyles));

export default connector(App);

interface Props {
  alert?: string;
  chatEnabled?: boolean;
  children: any;
  darkMode: boolean;
  displayHubMenu: boolean;
  isInvalid: boolean;
  isMobileChatOpen: boolean;
  isMobileMenuOpen: boolean;
  isNativeApp: boolean;
  isNativeWebView: boolean;
  hasPlayUpCampaign: boolean;
  onToggleCampaignModal: () => void;
  isPxcEnabled: boolean;
  isSigningIn: boolean;
  isSignedIn: boolean;
  location: Object;
  showCampaignModal: boolean;
  onToggleMobileMenu: () => void;
  saveUrlQuery: Object => void;
  sheet: Object;
  siteName: string;
  siteUrl: string;
  toggleChat: () => void;
  userId?: number;
  updateAppPlatform?: string;
}

const AppWrapper = glamorous.div(
  ({ theme, darkMode }) => ({
    backgroundColor: darkMode ? theme.color.darkBackground : theme.color.lightBackground,
    color: darkMode ? theme.color.basis6 : theme.color.invertableBasis6,
    position: 'relative'
  }),
  ({ padTop }) => ({
    paddingTop: padTop
  }),
  'appWrapper'
);

const LoadingOverlay = glamorous.div({
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  zIndex: 9999,
  margin: 'auto',
  backgroundColor: 'rgba(51, 51, 51, 0.75)'
});

const LoadingWrapper = glamorous.div({
  position: 'fixed',
  top: '50%',
  left: 0,
  right: 0,
  zIndex: 10000,
  margin: 'auto'
});
